import React, { useEffect, useMemo, useState } from "react";
import { http, authFileUrl } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, Search, Filter, Printer, Download, Star, AlertTriangle, Camera, CheckCircle2 } from "lucide-react";

export default function PeaceOfMindReports() {
  const [reports, setReports] = useState([]);
  const [q, setQ] = useState("");
  const [studentId, setStudentId] = useState("all");
  const [students, setStudents] = useState([]);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    http.get("/students").then(({ data }) => setStudents(data));
    load();
  }, [studentId]);

  async function load() {
    const url = `/reports/peace-of-mind${studentId && studentId !== "all" ? `?student_id=${studentId}` : ""}`;
    const { data } = await http.get(url);
    setReports(data);
  }

  const filtered = useMemo(() =>
    reports.filter(r => !q ||
      (r.comments || "").toLowerCase().includes(q.toLowerCase()) ||
      (r.cleaner_name || "").toLowerCase().includes(q.toLowerCase()) ||
      ((r.student || {}).name || "").toLowerCase().includes(q.toLowerCase())),
    [reports, q]);

  return (
    <div className="space-y-4" data-testid="peace-of-mind-reports">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Badge className="bg-secondary/10 text-secondary border border-secondary/20 mb-1"><ShieldCheck size={11} className="mr-1" /> Peace of mind</Badge>
          <h1 className="font-display text-2xl font-bold text-primary">Cleaning Reports Archive</h1>
          <p className="text-sm text-muted-foreground">Every visit, forever archived. Searchable, printable, downloadable.</p>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search cleaner, notes, student…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="pom-search" />
        </div>
        <Select value={studentId} onValueChange={setStudentId}>
          <SelectTrigger className="w-52" data-testid="pom-student-filter"><SelectValue placeholder="All students" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All students</SelectItem>
            {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <Card className="border-dashed"><CardContent className="p-10 text-center text-muted-foreground">
          No reports yet. After a cleaner submits a visit report, it will appear here — permanently archived.
        </CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {filtered.map((r) => <ReportCard key={r.id} report={r} onOpen={() => setViewing(r)} />)}
        </div>
      )}

      {viewing && <ReportDetail report={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function ReportCard({ report, onOpen }) {
  const s = report.overall_score || 0;
  const scoreColor = s >= 9 ? "text-secondary" : s >= 7 ? "text-primary" : "text-amber-600";
  // Prefer a room photo for the card thumbnail; fall back to legacy before/after
  const thumb1 = report.bedroom_photos?.[0] || report.before_photos?.[0];
  const thumb2 = report.bathroom_photos?.[0] || report.kitchen_photos?.[0] || report.after_photos?.[0];
  return (
    <Card className="border-border/60 hover:shadow-md transition-shadow cursor-pointer" onClick={onOpen} data-testid={`pom-card-${report.id}`}>
      <CardContent className="p-5">
        <div className="flex justify-between items-start">
          <div>
            <div className="font-display font-semibold text-primary">{report.student?.name || "—"}</div>
            <div className="text-xs text-muted-foreground">{report.property?.name}</div>
            <div className="text-xs text-muted-foreground mt-1">{(report.created_at || "").slice(0, 10)} · by {report.cleaner_name}</div>
          </div>
          <div className="text-right">
            <div className={`font-display text-3xl font-bold ${scoreColor}`}>{s}</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">/ 10</div>
          </div>
        </div>
        {(thumb1 || thumb2) && (
          <div className="grid grid-cols-2 gap-2 mt-3">
            {thumb1 && <div className="aspect-video bg-muted rounded overflow-hidden"><img src={authFileUrl(thumb1)} alt="room" className="w-full h-full object-cover" /></div>}
            {thumb2 && <div className="aspect-video bg-muted rounded overflow-hidden"><img src={authFileUrl(thumb2)} alt="room" className="w-full h-full object-cover" /></div>}
          </div>
        )}
        {report.comments && <p className="text-xs text-muted-foreground mt-3 line-clamp-2 italic">"{report.comments}"</p>}
        <div className="mt-3 flex gap-1.5 flex-wrap">
          {report.flags?.has_maintenance_issue && <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-[10px]"><AlertTriangle size={10} className="mr-0.5" /> Maintenance</Badge>}
          {report.flags?.has_damage && <Badge className="bg-red-50 text-red-700 border border-red-200 text-[10px]">Damage</Badge>}
          {!report.flags?.has_maintenance_issue && !report.flags?.has_damage && <Badge className="bg-secondary/10 text-secondary border border-secondary/20 text-[10px]"><CheckCircle2 size={10} className="mr-0.5" /> All clear</Badge>}
        </div>
      </CardContent>
    </Card>
  );
}

function ReportDetail({ report, onClose }) {
  return (
    <Dialog open={!!report} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="pom-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck size={18} className="text-secondary" /> Peace of Mind Report</DialogTitle>
        </DialogHeader>
        <div className="print-report space-y-5">
          <div className="bg-primary text-primary-foreground rounded-2xl p-6 text-center">
            <div className="text-[11px] uppercase tracking-widest opacity-80">Overall Room Score</div>
            <div className="font-display text-5xl font-bold mt-2">{report.overall_score} <span className="text-2xl opacity-60">/ 10</span></div>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <Info label="Student" value={report.student?.name} />
            <Info label="Property" value={report.property?.name} />
            <Info label="Cleaner" value={report.cleaner_name} />
            <Info label="Visit date" value={(report.created_at || "").slice(0, 10)} />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <ScoreTile n={report.cleanliness_score} l="Cleanliness" />
            <ScoreTile n={report.organization_score} l="Organization" />
            <ScoreTile n={report.flags?.has_maintenance_issue ? 7 : 10} l="Health & Safety" />
          </div>

          {report.maintenance_issues ? (
            <Section title="Maintenance observations" variant="warning">{report.maintenance_issues}</Section>
          ) : (
            <Section title="Maintenance observations" variant="success">No maintenance concerns were observed during today's visit.</Section>
          )}

          {report.comments && <Section title="Cleaner notes">"{report.comments}"</Section>}
          {report.recommendations && <Section title="Our recommendation" variant="tint">{report.recommendations}</Section>}

          {/* Room-by-room photos (new production requirement) */}
          {[
            {key: "bedroom_photos", label: "Bedroom", accent: "text-primary"},
            {key: "bathroom_photos", label: "Bathroom", accent: "text-primary"},
            {key: "kitchen_photos", label: "Kitchen", accent: "text-primary"},
            {key: "living_room_photos", label: "Living room", accent: "text-primary"},
            {key: "maintenance_photos", label: "Maintenance issues", accent: "text-amber-600"},
          ].map(({key, label, accent}) => (
            (report[key] || []).length > 0 && (
              <div key={key} data-testid={`pom-room-${key}`}>
                <div className={`font-display font-semibold mb-2 text-sm ${accent}`}>{label}</div>
                <div className="grid grid-cols-2 gap-2">
                  {(report[key] || []).map((u, i) => (
                    <a key={i} href={authFileUrl(u)} target="_blank" rel="noreferrer" className="block">
                      <div className="aspect-video bg-muted rounded overflow-hidden"><img src={authFileUrl(u)} alt={label} className="w-full h-full object-cover" /></div>
                    </a>
                  ))}
                </div>
              </div>
            )
          ))}

          {(report.before_photos?.length > 0 || report.after_photos?.length > 0) && (
            <div>
              <div className="font-display font-semibold text-primary mb-2 text-sm">Before &amp; after</div>
              <div className="grid grid-cols-2 gap-2">
                {(report.before_photos || []).map((u, i) => (
                  <a key={`b${i}`} href={authFileUrl(u)} target="_blank" rel="noreferrer" className="block">
                    <div className="aspect-video bg-muted rounded overflow-hidden"><img src={authFileUrl(u)} alt="before" className="w-full h-full object-cover" /></div>
                    <div className="text-[10px] text-muted-foreground text-center mt-1 uppercase">Before</div>
                  </a>
                ))}
                {(report.after_photos || []).map((u, i) => (
                  <a key={`a${i}`} href={authFileUrl(u)} target="_blank" rel="noreferrer" className="block">
                    <div className="aspect-video bg-muted rounded overflow-hidden"><img src={authFileUrl(u)} alt="after" className="w-full h-full object-cover" /></div>
                    <div className="text-[10px] text-secondary text-center mt-1 uppercase">After</div>
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-4 border-t no-print">
            <Button onClick={() => window.print()} variant="outline" data-testid="pom-print"><Printer size={14} className="mr-1" /> Print</Button>
            <Button onClick={() => window.print()} variant="outline" data-testid="pom-download"><Download size={14} className="mr-1" /> Save as PDF</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value }) {
  return <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div><div className="font-medium text-primary">{value || "—"}</div></div>;
}
function ScoreTile({ n, l }) {
  return <div className="bg-muted/40 rounded-lg p-3 text-center">
    <div className="font-display font-bold text-2xl text-secondary">{n || 0}</div>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div>
  </div>;
}
function Section({ title, children, variant }) {
  const cls = variant === "warning" ? "bg-amber-50 border-amber-200 text-amber-900" :
              variant === "success" ? "bg-secondary/10 border-secondary/30 text-primary" :
              variant === "tint" ? "bg-primary/5 border-primary/20 text-primary" :
              "bg-muted/40 border-border";
  return (
    <div>
      <div className="font-display font-semibold text-primary text-sm mb-1.5">{title}</div>
      <div className={`text-sm rounded-lg border p-3 ${cls}`}>{children}</div>
    </div>
  );
}
